import { ApiGetAllResponse } from '@core/services/interfaces/pagination.interface';
import { from, map, mergeMap, Observable, of, switchMap, toArray } from 'rxjs';

/**
 * Configuration options for the fetchAllBatch utility.
 */
export interface FetchBatchOptions {
    /** Maximum number of records to fetch across all pages. Defaults to 10000. */
    maxRecords?: number;
    /** Number of records per page. Defaults to 1000. */
    pageSize?: number;
    /** The key used for pagination limit (e.g., 'limit' or 'pageSize'). Defaults to 'limit'. */
    limitKey?: 'limit' | 'pageSize';
    /** Max concurrent requests. Defaults to 3 to prevent server overload. */
    concurrency?: number;
}

/**
 * A generic utility to fetch multiple pages of API data in batches until a limit is reached.
 * Optimized to fetch pages in parallel after the first page reveals the total count.
 *
 * @template T The entity type being fetched.
 * @template P The query parameters type.
 *
 * @param fetcher A function that makes the API call given a set of parameters.
 * @param params The initial query parameters.
 * @param options Optional configuration for max records and batch sizes.
 *
 * @returns An observable that emits the aggregated array of all collected records.
 */
export function fetchAllBatch<T, P extends object>(
    fetcher: (params: P) => Observable<ApiGetAllResponse<T>>,
    params: P,
    options: FetchBatchOptions = {},
): Observable<T[]> {
    const { maxRecords = 10000, pageSize = 1000, limitKey = 'limit', concurrency = 3 } = options;

    const initialParams = {
        ...params,
        [limitKey]: pageSize,
        page: 1,
    } as P;

    return fetcher(initialParams).pipe(
        switchMap((res) => {
            const totalCount = res.meta?.pagination?.totalCount ?? res.data.length;
            const totalToFetch = Math.min(totalCount, maxRecords);
            const totalPages = Math.ceil(totalToFetch / pageSize);

            if (totalPages <= 1) {
                return of(res.data);
            }

            const pageIndexes = [];
            for (let i = 2; i <= totalPages; i++) {
                pageIndexes.push(i);
            }

            // Process remaining pages with concurrency limit
            return from(pageIndexes).pipe(
                mergeMap((page) => {
                    const nextParams = {
                        ...params,
                        [limitKey]: pageSize,
                        page,
                    } as P;
                    return fetcher(nextParams);
                }, concurrency),
                toArray(),
                map((responses) => {
                    const allData = [...res.data];
                    // Sort responses by page to ensure data order if needed,
                    // though for many use cases order doesn't matter.
                    responses.forEach((r) => allData.push(...r.data));
                    return allData.slice(0, maxRecords);
                }),
            );
        }),
    );
}
